import React, { useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import MuLogoStacked from '../../assets/MuLogoStacked.svg';
import { fetchRecommendedCollections as fetchRecommendedCollectionsAction } from '../../actions/collections.actions';
import DashboardAllSubjectsCarousels from '../../components/Dashboard/DashboardAllSubjectsCarousels';
import constants, {
  routes,
  PINNED_CAROUSEL_LOWER_LIMIT,
  Subject,
} from '../../constants';
import DashboardSearchBar from '../../components/DashboardSearchBar';
import { useSelector } from 'react-redux';
import {
  selectRecommendedCollections,
  selectRecommendedCollectionsStatus
} from '../../reducers/collections.selectors';
import { selectPinnedContents, selectPinnedStatus } from '../../reducers/pinned.selectors';
import {
  selectRecommendedUserProducts,
  selectRecommendedProductsStatus
} from '../../reducers/recommendation.selectors';
import DashboardRecommendedProductsCarousel from '../../components/Dashboard/DashboardRecommendedProductsCarousel';
import DashboardPinnedDocumentsCarousel from '../../components/Dashboard/DashboardPinnedDocumentsCarousel';
import DashboardCuratedCollectionsCarousel from '../../components/Dashboard/DashboardCuratedCollectionsCarousel';
import querystring from 'query-string';
import TopBar from '../../components/AppLayout/TopBar';
import UpgradeBanner from '../../components/UpgradeBanner';
import { useInView } from 'react-intersection-observer';
import { selectIsOnboardingCompleted, selectUser } from '../../reducers/user.selectors';
import useRouter from '../../utils/useRouter';
import useAction from '../../utils/useAction';
import { getAmpliSearchEventPayload, mapDesignPreference } from '../../utils/ampli';
import { SearchSubmitted } from '../../ampli';
import services from '../../services';
import DashboardStudyflixCarousel from '../../components/Dashboard/DashboardStudyflixCarousel';
import { Box } from '@mui/material';
import { spaces } from '../../assets/styles/themes/tokens';
import { useNewDesign } from '../../state/DesignPreferenceContextProvider';
import DesignSwitchFeedbackDialog from '../../components/modals/FeedbackDialogs/DesignSwitchFeedbackDialog';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import { useNewNavigation } from '../../utils/useNewNavigation';

const filterSubjectList = (allSubjects: Subject[], userSubjects: Subject[]) => {
  if (!userSubjects) {
    return allSubjects;
  }

  return allSubjects.filter(s => !userSubjects.find(us => s.id === us.id));
};

export const carouselBreakpoints = {
  product: {
    xl: 4,
    lg: 3,
    md: 2.2,
    sm: 1.5,
    xs: 1.1,
    xxs: 1
  },
  collection: {
    xl: 3,
    lg: 2,
    md: 1,
    sm: 1,
    xs: 1,
    xxs: 1
  }
};

const DashboardPage: React.FC = () => {
  const user = useSelector(selectUser);
  const hasDocumentLevelAccess = !!(user && user.hasDocumentLevelAccess);
  const { router } = useRouter();
  const showNewNavigation = useNewNavigation();
  const { prefersNewDesign, shouldShowDesignPreferenceSwitch } = useNewDesign();
  const fetchRecommendedCollections = useAction(fetchRecommendedCollectionsAction);
  const isOnboardingCompleted = useSelector(selectIsOnboardingCompleted);
  const randomPinnedDocs = useSelector(selectPinnedContents);
  const pinnedStatus = useSelector(selectPinnedStatus);
  const recommendedProducts = useSelector(selectRecommendedUserProducts);
  const recommendedProductsStatus = useSelector(selectRecommendedProductsStatus);
  const recommendedCollectionsStatus = useSelector(selectRecommendedCollectionsStatus);
  const recommendedCollections = useSelector(selectRecommendedCollections);

  useEffect(() => {
    if (!isOnboardingCompleted) {
      router.replace(`/${routes.createProfile.root}/${routes.createProfileSubjects.root}`);
    }
  }, [isOnboardingCompleted]);

  useEffect(() => {
    fetchRecommendedCollections();
  }, [user?.id]);

  const submitSearchCallback = useCallback((query: string, subject: string | null) => {
    const selectedSubject = subject && constants.subjectsById[subject];
    const term: string = query;
    if (!query && !selectedSubject) {
      return router.push(`/${routes.search.root}`);
    }

    const qs = querystring.stringify({
      term: query ? query : undefined,
      subject: (selectedSubject && selectedSubject.id),
    }, {
      skipNull: true,
    });

    if (user) {
      const searchPayload = getAmpliSearchEventPayload({
        query: Object.fromEntries(new URLSearchParams(qs)),
        term,
        appSection: 'library',
      });

      if (searchPayload) {
        const event = new SearchSubmitted(searchPayload as any);
        if (shouldShowDesignPreferenceSwitch) {
          event.event_properties['ui'] = mapDesignPreference(prefersNewDesign);
        }
        services.track.ampliEventTrack({ event });
      }
    }

    return router.push(`/${routes.search.root}?${qs}`);
  }, [user?.id, shouldShowDesignPreferenceSwitch]);

  const userSubjects = user?.subjectsFlattened;
  const allSubjectsList = filterSubjectList(constants.subjects, userSubjects || []);

  const { ref, inView } = useInView({
    // Due to the scroll behaviour of the dashboard, we were
    // unable to set the intersection observer from the navigation bar.
    // This height represents the height of the navigation bar and half the
    // height of the search bar
    rootMargin: '-70px',
    initialInView: true,
  });

  const showRecommendationCarousel = recommendedProducts.length >= PINNED_CAROUSEL_LOWER_LIMIT;
  const showPinnedCarousel = randomPinnedDocs.length >= PINNED_CAROUSEL_LOWER_LIMIT;
  const recommendedProductsPosition = 1;
  const subjectCarouselPositions = recommendedProductsPosition + (showRecommendationCarousel ? 1 : 0);
  const studyflixCarouselPosition = recommendedProductsPosition + 1;
  const pinnedCarouselPosition = subjectCarouselPositions + (userSubjects?.length || 0);
  const recommendedCollectionsPosition = pinnedCarouselPosition + (showPinnedCarousel ? 1 : 0);

  const content = (
    <div className='dashboard-page'>
      <DesignSwitchFeedbackDialog />
      <UpgradeBanner />
      { !showNewNavigation && <TopBar hideSearch={inView} /> }
      <ContainerWrapper showNewNavigation={showNewNavigation}>
        {
          !showNewNavigation &&
          <Row>
            <Col xs={6} sm={5} lg={3} className='m-5 mx-auto logo-col'>
              <MuLogoStacked />
            </Col>
          </Row>
        }
        {
          !showNewNavigation &&
          <div ref={ref}>
            <Row className='mb-5'>
              <Col>
                <DashboardSearchBar
                  mainSubjects={userSubjects || []}
                  otherSubjects={allSubjectsList}
                  onSubmit={submitSearchCallback}
                />
              </Col>
            </Row>
          </div>
        }
        {hasDocumentLevelAccess &&
          <DashboardStudyflixCarousel
            numberOfSlides={carouselBreakpoints.product}
            carouselPosition={studyflixCarouselPosition}
          />
        }
        {showRecommendationCarousel &&
          <DashboardRecommendedProductsCarousel
            carouselPosition={recommendedProductsPosition}
            status={recommendedProductsStatus}
            items={recommendedProducts}
            numberOfSlides={carouselBreakpoints.product}
          />
        }
        <DashboardAllSubjectsCarousels
          firstCarouselPosition={subjectCarouselPositions}
          numberOfSlides={carouselBreakpoints.product}
        />
        {showPinnedCarousel &&
          <DashboardPinnedDocumentsCarousel
            carouselPosition={pinnedCarouselPosition}
            status={pinnedStatus}
            items={randomPinnedDocs}
            numberOfSlides={carouselBreakpoints.product}
          />
        }
        <DashboardCuratedCollectionsCarousel
          carouselPosition={recommendedCollectionsPosition}
          numberOfSlides={carouselBreakpoints.collection}
          status={recommendedCollectionsStatus}
          collections={recommendedCollections}
        />
      </ContainerWrapper>
    </div>
  );

  return showNewNavigation ? (
    <MasterLayout>
      {content}
    </MasterLayout>
  ) : content;
};

export default DashboardPage;

const ContainerWrapper = ({
  children,
  showNewNavigation
}: {
  children: React.ReactNode,
  showNewNavigation: boolean
}) => {
  return showNewNavigation ? (
    <Box sx={{ paddingTop: spaces.md, paddingX: spaces.md }}>
      {children}
    </Box>
  ) : <Container className='pb-4'>{children}</Container>;
};
